import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

export interface FilterOption {
  label: string;
  value: string;
  count?: number;
  color?: string;
}

export interface DateRange {
  start: string;
  end: string;
}

export interface AdvancedFilterConfig {
  searchTerm: string;
  status: string[];
  priority: string[];
  category: string[];
  dateRange: DateRange;
  durationRange: [number, number];
  urgencyLevel: string[];
  resolutionStatus: string[];
}

interface AdvancedFilterProps {
  onFilterChange: (filters: AdvancedFilterConfig) => void;
  onSearchChange: (searchTerm: string) => void;
  searchTerm: string;
  availableOptions: {
    statuses: FilterOption[];
    priorities: FilterOption[];
    categories: FilterOption[];
    urgencyLevels: FilterOption[];
    resolutionStatuses: FilterOption[];
  };
  showAdvanced?: boolean;
}

const AdvancedFilter: React.FC<AdvancedFilterProps> = ({
  onFilterChange,
  onSearchChange,
  searchTerm,
  availableOptions,
  showAdvanced = false
}) => {
  const [expanded, setExpanded] = useState(showAdvanced);
  const [filters, setFilters] = useState<AdvancedFilterConfig>({
    searchTerm: searchTerm,
    status: [],
    priority: [],
    category: [],
    dateRange: { start: '', end: '' },
    durationRange: [0, 3600],
    urgencyLevel: [],
    resolutionStatus: []
  });

  const updateFilters = (newFilters: Partial<AdvancedFilterConfig>) => {
    const updated = { ...filters, ...newFilters };
    setFilters(updated);
    onFilterChange(updated);
  };

  const handleSearchChange = (value: string) => {
    updateFilters({ searchTerm: value });
    onSearchChange(value);
  };

  const clearAllFilters = () => {
    const cleared: AdvancedFilterConfig = {
      searchTerm: '',
      status: [],
      priority: [],
      category: [],
      dateRange: { start: '', end: '' },
      durationRange: [0, 3600],
      urgencyLevel: [],
      resolutionStatus: []
    };
    setFilters(cleared);
    onFilterChange(cleared);
    onSearchChange('');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.status.length > 0) count++;
    if (filters.priority.length > 0) count++;
    if (filters.category.length > 0) count++;
    if (filters.urgencyLevel.length > 0) count++;
    if (filters.resolutionStatus.length > 0) count++;
    return count;
  };

  return (
    <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}>
      <Box sx={{ p: 2 }}>
        {/* Search Bar */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: expanded ? 2 : 0 }}>
          <TextField
            fullWidth
            placeholder="Search calls by customer name, phone, issue, or call ID..."
            value={filters.searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666666' }} />
                </InputAdornment>
              ),
              endAdornment: filters.searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => handleSearchChange('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2'
                }
              }
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setExpanded(!expanded)}
            sx={{
              borderColor: '#e0e0e0',
              color: '#666666',
              minWidth: 120,
              '&:hover': {
                borderColor: '#1976d2',
                color: '#1976d2'
              }
            }}
          >
            Filters
            {getActiveFilterCount() > 0 && (
              <Chip
                label={getActiveFilterCount()}
                size="small"
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: '#1976d2',
                  color: 'white'
                }}
              />
            )}
          </Button>
          {getActiveFilterCount() > 0 && (
            <Button variant="text" size="small" onClick={clearAllFilters}>
              Clear All
            </Button>
          )}
        </Box>

        {/* Advanced Filters */}
        <Collapse in={expanded}>
          <Box sx={{ pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    multiple
                    value={filters.status}
                    onChange={(e) => updateFilters({ status: e.target.value as string[] })}
                    label="Status"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {availableOptions.statuses.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label} {option.count !== undefined && `(${option.count})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    multiple
                    value={filters.priority}
                    onChange={(e) => updateFilters({ priority: e.target.value as string[] })}
                    label="Priority"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {availableOptions.priorities.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label} {option.count !== undefined && `(${option.count})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    multiple
                    value={filters.category}
                    onChange={(e) => updateFilters({ category: e.target.value as string[] })}
                    label="Category"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {availableOptions.categories.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label} {option.count !== undefined && `(${option.count})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Urgency</InputLabel>
                  <Select
                    multiple
                    value={filters.urgencyLevel}
                    onChange={(e) => updateFilters({ urgencyLevel: e.target.value as string[] })}
                    label="Urgency"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {availableOptions.urgencyLevels.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label} {option.count !== undefined && `(${option.count})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Box>
    </Card>
  );
};

export default AdvancedFilter;

